import { useLayoutEffect, useRef } from 'react'
import { SlashMenuItemsGroup } from 'proseShared/SlashMenuItems'
import Styles from './FloatingMenuItemsContainer.module.scss'
import { KeyboardShortcutTag } from 'common/KeyboardShortcutTag/KeyboardShortcutTag'

interface Props {
  onItemClick: (idx: number) => void
  setSelected: (idx: number) => void
  items: SlashMenuItemsGroup[]
  selected: number
}

export const FloatingMenuItemsContainer = ({ items, selected, setSelected, onItemClick }: Props) => {
  const elementsIndex = items.flatMap((item) => item.elements)
  const selfRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!selfRef.current) return
    const selectedItem = selfRef.current.querySelector(`.${Styles.selectedItem}`)
    if (selectedItem) selectedItem.scrollIntoView({ block: 'nearest' })
  }, [selected])

  const markdownKeys = elementsIndex[selected]?.markdown?.split('').map((e) => (e === ' ' ? 'space' : e)) || [
    'No markdown',
  ]

  return (
    <div className={Styles.container}>
      <div className={Styles.promptContainer} ref={selfRef}>
        {items.map((group) => {
          if (!group.elements.length) return null

          return (
            <div key={group.title} className={Styles.groupContainer}>
              {group.hideTitle || <p>{group.title}</p>}
              {group.elements.map((c, i) => {
                const elemIndex = elementsIndex.findIndex((elem) => elem === c)
                const isSelected = selected === elemIndex

                return (
                  <div
                    key={elemIndex}
                    tabIndex={elemIndex}
                    className={isSelected ? Styles.selectedItem : Styles.commandListItem}
                    onMouseEnter={() => setSelected(elemIndex)}
                    onClick={() => onItemClick(elemIndex)}
                    data-command-line-selected-item={!!isSelected}
                  >
                    <div className={Styles.iconContainer}>{c.icon}</div>
                    {c.label}
                    {isSelected && c.keymap && (
                      <KeyboardShortcutTag {...c.keymap} className={Styles.keyboardShortcutTag} />
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
        {!items.length && <div className={Styles.noResultsContainer}>No results</div>}
      </div>

      {elementsIndex[selected] && (
        <div className={Styles.markdownContainer}>
          <span>Markdown:</span>
          {markdownKeys.map((key, i) => (
            <span key={i} className={Styles.markdownShortcut}>
              {key}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
